// Release A — Admin News CMS.
//
// Visual admin for /api/admin/news/articles. Create / edit / publish /
// unpublish drafts. Backs the Phase 3 editorial workflow — AI-generated
// drafts land in `status=draft` and must be approved here before they
// render on /lifted-views-news.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, Plus, Loader2, Save, Trash2, Eye, Sparkles, ExternalLink, Search } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { NewsIngestionPanel } from "@/components/news/NewsIngestionPanel";
import { NewsPipelinePanel } from "@/components/news/NewsPipelinePanel";

const CATEGORIES = [
  "cannabis", "hemp_cbd", "vapes_carts", "nicotine_disposables",
  "state_policy", "business", "culture",
];
const POLICY = ["", "proposed", "enacted", "effective", "blocked"];
const STATUSES = ["draft", "published", "unpublished"];

const BLANK = {
  headline: "", summary: "", body: "",
  category: "state_policy", state: "",
  source_url: "", source_publisher: "", source_published_at: "",
  policy_status: "", jurisdiction: "", affected_products: [],
  ai_assisted: false, status: "draft", featured: false,
};

export default function AdminNewsCMS() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      const { data } = await api.get(`/admin/news/articles?${params}`);
      setList(data.articles || []);
    } catch (e) {
      setErr(apiErrorMessage(e, "Could not load articles"));
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q) return list;
    const needle = q.toLowerCase();
    return list.filter((a) =>
      (a.headline + " " + a.summary + " " + (a.source_publisher || ""))
        .toLowerCase().includes(needle));
  }, [list, q]);

  const counts = useMemo(() => ({
    all: list.length,
    draft: list.filter((a) => a.status === "draft").length,
    published: list.filter((a) => a.status === "published").length,
    unpublished: list.filter((a) => a.status === "unpublished").length,
  }), [list]);

  async function save() {
    if (!editing) return;
    setSaving(true); setErr("");
    try {
      if (editing.id) {
        const { id, slug, created_at, ...rest } = editing;
        await api.patch(`/admin/news/articles/${id}`, rest);
      } else {
        await api.post("/admin/news/articles", editing);
      }
      await load();
      setEditing(null);
    } catch (e) {
      setErr(apiErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  }

  async function softDelete(a) {
    if (!window.confirm(`Unpublish "${a.headline}"?`)) return;
    try {
      await api.delete(`/admin/news/articles/${a.id}`);
      await load();
    } catch (e) {
      setErr(apiErrorMessage(e, "Delete failed"));
    }
  }

  return (
    <div className="p-4 lg:p-6 min-h-screen bg-[#06080b] text-white" data-testid="admin-news-cms">
      <div className="flex items-center gap-3 mb-6">
        <Newspaper className="h-6 w-6 text-lifted-green" />
        <h1 className="font-anton uppercase text-2xl text-white">Lifted Views News CMS</h1>
        <button onClick={() => setEditing({ ...BLANK })} data-testid="new-article-btn"
          className="ml-auto btn-lifted text-xs inline-flex items-center gap-1">
          <Plus className="h-3 w-3" /> New article
        </button>
      </div>

      {!editing && (
        <>
          <NewsIngestionPanel onRunComplete={load} />
          <NewsPipelinePanel />
          <div className="flex flex-wrap gap-3 items-center mb-4">
            <div className="flex gap-1 bg-black/40 border border-white/10 rounded p-1">
              <StatusTab active={!filter} onClick={() => setFilter("")} label={`All (${counts.all})`} testid="news-tab-all" />
              <StatusTab active={filter === "draft"} onClick={() => setFilter("draft")} label={`Drafts (${counts.draft})`} testid="news-tab-draft" />
              <StatusTab active={filter === "published"} onClick={() => setFilter("published")} label={`Published (${counts.published})`} testid="news-tab-published" />
              <StatusTab active={filter === "unpublished"} onClick={() => setFilter("unpublished")} label={`Archived (${counts.unpublished})`} testid="news-tab-archived" />
            </div>
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="h-3 w-3 absolute left-3 top-3 text-white/50" />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search headlines, summaries, publishers…"
                className="input-lifted w-full pl-8 text-sm" data-testid="news-search" />
            </div>
          </div>

          {err && <div className="glass-panel p-3 mb-4 border border-rose-500/40 text-rose-200 text-sm" data-testid="news-error">{err}</div>}
          {loading ? <Loader /> : (
            <div className="glass-panel overflow-hidden">
              <table className="w-full text-xs" data-testid="news-table">
                <thead className="bg-black/40 text-white/60">
                  <tr>
                    <th className="text-left px-3 py-2">Headline</th>
                    <th className="text-left px-3 py-2">Category</th>
                    <th className="text-left px-3 py-2">State</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Source</th>
                    <th className="text-left px-3 py-2">AI</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-10 text-white/50" data-testid="news-empty">
                      No articles yet — click "New article" or wait for the next ingestion run.
                    </td></tr>
                  ) : filtered.map((a) => (
                    <tr key={a.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`news-row-${a.id}`}>
                      <td className="px-3 py-2">
                        <button onClick={() => setEditing(a)}
                          data-testid={`news-edit-${a.id}`}
                          className="text-white text-left hover:text-lifted-green">
                          {a.headline}
                        </button>
                        <div className="text-[10px] text-white/40">{a.slug}</div>
                      </td>
                      <td className="px-3 py-2 text-white/70 capitalize">{a.category.replace(/_/g, " ")}</td>
                      <td className="px-3 py-2 text-white/60">{a.state || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded border text-[10px] font-bebas uppercase tracking-wider ${
                          a.status === "published" ? "border-lifted-green/40 text-lifted-green"
                          : a.status === "draft" ? "border-amber-400/40 text-amber-300"
                          : "border-white/20 text-white/50"
                        }`}>{a.status}</span>
                        {a.featured && <span className="ml-1 text-[9px] text-lifted-green">★</span>}
                      </td>
                      <td className="px-3 py-2 text-white/60 truncate max-w-[160px]">{a.source_publisher || "—"}</td>
                      <td className="px-3 py-2">{a.ai_assisted ? <Sparkles className="h-3 w-3 text-lifted-green" /> : null}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {a.status === "published" && (
                          <Link to={`/lifted-views-news/${a.slug}`} target="_blank"
                            data-testid={`news-view-${a.id}`}
                            className="inline-flex items-center text-white/60 hover:text-lifted-green text-[11px] gap-1 mr-2">
                            <Eye className="h-3 w-3" /> View
                          </Link>
                        )}
                        <button onClick={() => softDelete(a)} data-testid={`news-del-${a.id}`}
                          className="inline-flex items-center text-white/60 hover:text-rose-300 text-[11px] gap-1">
                          <Trash2 className="h-3 w-3" /> Archive
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {editing && (
        <ArticleEditor
          value={editing} setValue={setEditing}
          onCancel={() => setEditing(null)}
          onSave={save} saving={saving}
        />
      )}
    </div>
  );
}

function StatusTab({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-[10px] font-bebas uppercase tracking-[0.2em] px-3 py-1.5 rounded ${
        active ? "bg-lifted-green text-black" : "text-white/70 hover:text-white"
      }`}>{label}</button>
  );
}

function Loader() {
  return <div className="flex items-center gap-2 text-white/50 text-sm">
    <Loader2 className="h-4 w-4 animate-spin" /> Loading…
  </div>;
}

function ArticleEditor({ value, setValue, onCancel, onSave, saving }) {
  const set = (k, v) => setValue((p) => ({ ...p, [k]: v }));
  return (
    <div className="glass-panel p-6 max-w-4xl mx-auto" data-testid="article-editor">
      <div className="flex items-center gap-3 mb-4">
        <div className="font-anton uppercase text-xl">
          {value.id ? "Edit Article" : "New Article"}
        </div>
        {value.ai_assisted && <span className="text-[10px] font-bebas uppercase tracking-wider text-lifted-green border border-lifted-green/40 rounded px-2 py-0.5">AI draft</span>}
        <button onClick={onCancel} data-testid="editor-cancel"
          className="ml-auto text-xs text-white/60 hover:text-white">← Back to list</button>
      </div>

      <div className="grid gap-3">
        <Lbl l="Headline *">
          <input value={value.headline} onChange={(e) => set("headline", e.target.value)}
            data-testid="editor-headline" className="input-lifted w-full" />
        </Lbl>
        <Lbl l="Summary *">
          <textarea value={value.summary} onChange={(e) => set("summary", e.target.value)}
            data-testid="editor-summary" className="input-lifted w-full min-h-[80px]" />
        </Lbl>
        <Lbl l="Body *">
          <textarea value={value.body} onChange={(e) => set("body", e.target.value)}
            data-testid="editor-body" className="input-lifted w-full min-h-[240px] font-mono text-[13px]" />
        </Lbl>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Lbl l="Category *">
            <select value={value.category} onChange={(e) => set("category", e.target.value)}
              data-testid="editor-category" className="input-lifted w-full">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
            </select>
          </Lbl>
          <Lbl l="State (2-letter)">
            <input value={value.state} onChange={(e) => set("state", e.target.value.toUpperCase().slice(0, 2))}
              data-testid="editor-state" className="input-lifted w-full" maxLength={2} />
          </Lbl>
          <Lbl l="Policy status">
            <select value={value.policy_status || ""} onChange={(e) => set("policy_status", e.target.value)}
              data-testid="editor-policy-status" className="input-lifted w-full">
              {POLICY.map((p) => <option key={p} value={p}>{p || "— none —"}</option>)}
            </select>
          </Lbl>
          <Lbl l="Jurisdiction">
            <input value={value.jurisdiction || ""} onChange={(e) => set("jurisdiction", e.target.value)}
              data-testid="editor-jurisdiction" className="input-lifted w-full" />
          </Lbl>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Lbl l="Source URL">
            <input value={value.source_url || ""} onChange={(e) => set("source_url", e.target.value)}
              data-testid="editor-source-url" className="input-lifted w-full" />
          </Lbl>
          <Lbl l="Source publisher">
            <input value={value.source_publisher || ""} onChange={(e) => set("source_publisher", e.target.value)}
              data-testid="editor-source-publisher" className="input-lifted w-full" />
          </Lbl>
          <Lbl l="Source published date">
            <input type="date" value={(value.source_published_at || "").slice(0, 10)}
              onChange={(e) => set("source_published_at", e.target.value)}
              data-testid="editor-source-date" className="input-lifted w-full" />
          </Lbl>
        </div>

        <div className="flex flex-wrap gap-5 items-center">
          <Chk checked={value.ai_assisted} onChange={(v) => set("ai_assisted", v)}
            label="AI-assisted draft" testid="editor-ai" />
          <Chk checked={value.featured} onChange={(v) => set("featured", v)}
            label="Featured" testid="editor-featured" />
          <Lbl l="Status" inline>
            <select value={value.status} onChange={(e) => set("status", e.target.value)}
              data-testid="editor-status" className="input-lifted">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Lbl>
          {value.source_url && (
            <a href={value.source_url} target="_blank" rel="noopener noreferrer"
              className="ml-auto text-xs text-lifted-green inline-flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> View source
            </a>
          )}
        </div>

        <div className="flex gap-2 mt-2">
          <button onClick={onSave} disabled={saving || !value.headline || !value.body}
            data-testid="editor-save" className="btn-lifted disabled:opacity-50 inline-flex items-center gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Saving…" : (value.status === "published" ? "Publish" : "Save draft")}
          </button>
          <button onClick={onCancel} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function Lbl({ l, children, inline = false }) {
  if (inline) return (
    <label className="flex items-center gap-2 text-xs text-white/70">
      <span className="font-bebas tracking-[0.18em] uppercase">{l}</span>{children}
    </label>
  );
  return (
    <div>
      <label className="block text-xs font-bebas tracking-[0.18em] uppercase text-white/70 mb-1">{l}</label>
      {children}
    </div>
  );
}
function Chk({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs text-white/80 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
        data-testid={testid} className="accent-lifted-green" />
      {label}
    </label>
  );
}
